// Generated from remoat-platform/packages/usage-tracker/src/usage-tracker.ts by scripts/sync-usage-tracker.cjs.
// Do not edit here: change it there, test it there, and copy it again.

/**
 * Usage tracker shared by every MedicAI app. It records how the app is used and sends it to REMOAT in batches:
 * pages and the active time on them, clicks and taps, rage clicks (three quick clicks on one spot), dead clicks
 * (nothing on screen changed), dialogs closed without confirming, validation messages shown, server calls added up per
 * endpoint, named flows and errors.
 *
 * It never records what anyone types, page addresses with ids in them, or text from the page's content. Elements are
 * named by `data-track`, their component, icon, field name and classes; visible text is taken only inside navigation,
 * tabs, menus, toolbars and dialog buttons, where it is the app's own wording.
 *
 * Nothing is sent while the person is idle. A batch goes when a hundred events have built up, when something happens
 * five minutes or more after the last batch, and when the tab is hidden or closed (by beacon). A batch that could not be sent
 * is kept on the device and sent with the next one.
 *
 * It must never get in anyone's way. Every call and every listener swallows its own failures, nothing is ever logged,
 * a batch that cannot be sent is kept (within a small limit) or dropped, and every change on screen is watched only
 * for a second after each click.
 *
 * Framework free and dependency free, so every app can carry this one file.
 */

export type FlowStage = 'start' | 'done';

export interface UsageTrackerOptions {
  /** REMOAT's ingest address, e.g. https://remoat.medicai.co.za/api/usage */
  endpoint: string;
  appVersion: string;
  /** Asks the app's own backend for a usage pass; null when there is no signed-in person or tracking is off. */
  getPass: () => Promise<string | null>;
  /** The current page as a pattern, e.g. '/patient/:id/invoices'. Defaults to the address with ids replaced. */
  route?: () => string;
  /** Where visible text may be used to name what was clicked. */
  textZones?: string;
  now?: () => number;
}

export interface UsageTracker {
  start(): void;
  stop(): void;
  /** Call after every route change. */
  navigated(): void;
  /** A named piece of work started or finished, e.g. flow('invoice', 'start'). */
  flow(name: string, stage: FlowStage): void;
  /** Something the app names itself, counted with the clicks on the current page, e.g. action('Lab order resubmit'). */
  action(name: string): void;
  /** One server call: added up per endpoint and sent with the next batch. */
  api(method: string, url: string, ms: number, status: number): void;
  error(error: unknown): void;
  /** The person or practice changed (sign-in, sign-out, practice switch): fetch a new pass before the next batch. */
  passChanged(): void;
  /** Sends what has built up now. */
  flush(): Promise<void>;
}

export type UsageEvent = Record<string, string | number> & { t: string; at: number; r: string };

export const DEFAULT_TEXT_ZONES = [
  'nav', '[role=navigation]', '[role=tablist]', '[role=menu]', '[role=toolbar]', 'mat-toolbar', 'header',
  'mat-dialog-actions', '.mat-mdc-dialog-actions', '[data-track-text]',
].join(',');

const BATCH_SIZE = 100;
const BATCH_EVERY_MS = 5 * 60_000;
const IDLE_MS = 60_000;
const DEAD_AFTER_MS = 1000;
const SESSION_IDLE_MS = 30 * 60_000;
const PASS_MARGIN_MS = 10 * 60_000;
const PASS_RETRY_MS = 10 * 60_000;
const SLOW_MS = 2000;
const MAX_QUEUE = 500;
const MAX_UNSENT = 20;
/** Unsent batches kept on the device stay this small, so they never crowd out what the app itself stores. */
const MAX_UNSENT_CHARS = 100_000;
const MAX_ERRORS_PER_PAGE = 20;
/** A send or a pass request that takes longer than this is given up on (the batch is kept for next time). */
const WAIT_MS = 10_000;
const UNSENT_KEY = 'usage-tracker-unsent';
const SESSION_KEY = 'usage-tracker-session';

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/** A path as a pattern: ids, numbers and long tokens become ':id', and the query and hash are dropped. */
export function routePattern(path: string): string {
  const bare = path.split(/[?#]/)[0] ?? '';
  const parts = bare.split('/').filter(Boolean).map((part) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(part) || /\d{3,}/.test(part) || /^\d+$/.test(part) || (part.length > 20 && /\d|[A-Z]/.test(part)) ? ':id' : part);
  return `/${parts.join('/')}`;
}

/** Short, single-line text with numbers, emails and ids taken out. */
export function scrub(text: string, max = 40): string {
  return text
    .replace(/\s+/g, ' ')
    .replace(/[^\s@]+@[^\s@]+/g, '@')
    .replace(UUID, ':id')
    .replace(/\d+/g, '#')
    .trim()
    .slice(0, max);
}

export function viewportBucket(width: number): 'phone' | 'tablet' | 'laptop' | 'wide' {
  if (width < 600) return 'phone';
  if (width < 1024) return 'tablet';
  if (width < 1440) return 'laptop';
  return 'wide';
}

/** When a pass expires, in milliseconds; 0 when it cannot be read. */
export function passExpiry(pass: string): number {
  try {
    const part = pass.split('.')[1] ?? '';
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === 'number' ? exp * 1000 : 0;
  } catch {
    return 0;
  }
}

const INTERACTIVE = [
  'a', 'button', 'input', 'select', 'textarea', 'label', 'summary', '[data-track]',
  '[role=button]', '[role=link]', '[role=tab]', '[role=menuitem]', '[role=option]', '[role=checkbox]', '[role=switch]', '[role=radio]',
  'mat-option', 'mat-checkbox', 'mat-slide-toggle', 'mat-select', 'mat-chip',
].join(',');

const FRAMEWORK_PREFIX = /^(mat|mdc|cdk|ng)-/i;

/** The nearest component (custom element) around an element, e.g. 'app-patient-list'. */
function hostOf(el: Element): string {
  for (let at: Element | null = el.parentElement; at; at = at.parentElement) {
    const tag = at.tagName.toLowerCase();
    if (tag.includes('-') && !FRAMEWORK_PREFIX.test(tag)) return tag;
  }
  return '';
}

function iconOf(el: Element): string {
  const icon = el.matches('mat-icon') ? el : el.querySelector('mat-icon, .material-icons, .material-symbols-outlined');
  if (!icon) return '';
  return scrub(icon.getAttribute('fonticon') || icon.getAttribute('svgicon') || icon.textContent || '', 30);
}

function classesOf(el: Element): string[] {
  return Array.from(el.classList).filter((c) => !FRAMEWORK_PREFIX.test(c) && !/\d/.test(c) && !c.startsWith('cdk')).slice(0, 2);
}

function kindOf(el: Element): string {
  const role = el.getAttribute('role');
  return role && !['presentation', 'none'].includes(role) ? role : el.tagName.toLowerCase();
}

/**
 * What was clicked, as a name safe to send: `data-track` when the element or a parent has one, otherwise its
 * component, kind, icon, field name, wording (only inside the text zones) and classes.
 */
export function describeElement(target: Element, textZones: string = DEFAULT_TEXT_ZONES): { name: string; interactive: boolean } {
  const tracked = target.closest('[data-track]');
  if (tracked) return { name: scrub(tracked.getAttribute('data-track') || '', 80), interactive: true };
  const hit = target.closest(INTERACTIVE);
  const el = hit ?? target;
  const parts = [kindOf(el)];
  const icon = iconOf(el);
  if (icon) parts.push(`[${icon}]`);
  const field = el.getAttribute('formcontrolname') || el.getAttribute('name');
  if (field && /^(input|select|textarea|mat-select)$/i.test(el.tagName)) parts.push(`(${scrub(field, 30)})`);
  else if (el.closest(textZones)) {
    const text = scrub(el.getAttribute('aria-label') || (el.textContent || '').replace(icon, ''));
    if (text) parts.push(`"${text}"`);
  }
  const classes = classesOf(el);
  if (classes.length) parts.push(`.${classes.join('.')}`);
  const host = hostOf(el);
  return { name: `${host ? `${host} › ` : ''}${parts.join(' ')}`.slice(0, 120), interactive: !!hit };
}

/** Active time: the gaps between activity, each counted up to the idle limit, and nothing while hidden. */
export class ActiveClock {
  private total = 0;
  private last: number | null = null;

  constructor(private readonly idleMs = IDLE_MS) {}

  activity(now: number): void {
    if (this.last !== null) this.total += Math.min(Math.max(0, now - this.last), this.idleMs);
    this.last = now;
  }

  pause(now: number): void {
    if (this.last !== null) this.activity(now);
    this.last = null;
  }

  /** The time counted so far, starting the count again. */
  take(now: number): number {
    if (this.last !== null) this.activity(now);
    const total = this.total;
    this.total = 0;
    return total;
  }
}

/** Three clicks within a second and 30 pixels of each other: true on the third, once per burst. */
export class RageClicks {
  private clicks: { x: number; y: number; at: number }[] = [];
  private flagged = false;

  click(x: number, y: number, at: number): boolean {
    const near = this.clicks.filter((c) => at - c.at <= 1000 && Math.hypot(c.x - x, c.y - y) <= 30);
    if (!near.length) this.flagged = false;
    this.clicks = [...near, { x, y, at }];
    if (this.clicks.length >= 3 && !this.flagged) {
      this.flagged = true;
      return true;
    }
    return false;
  }
}

/** Buttons that confirm a dialog: submit, primary, marked, or worded like it. */
const CONFIRMING = /^(save|create|submit|confirm|send|ok|done|apply|add|update|yes|continue|finish|book|generate|sign|accept)\b/i;
const isConfirming = (el: Element | null): boolean => !!el && (
  el.getAttribute('type') === 'submit' || el.classList.contains('mat-primary') || el.getAttribute('color') === 'primary'
  || el.hasAttribute('data-track-done') || CONFIRMING.test((el.textContent || '').trim()));

function dialogName(dialog: Element): string {
  const tracked = dialog.getAttribute('data-track') || dialog.querySelector('[data-track-dialog]')?.getAttribute('data-track-dialog');
  if (tracked) return scrub(tracked, 80);
  const walker = document.createTreeWalker(dialog, NodeFilter.SHOW_ELEMENT);
  for (let node = walker.nextNode() as Element | null; node; node = walker.nextNode() as Element | null) {
    const tag = node.tagName.toLowerCase();
    if (tag.includes('-') && !FRAMEWORK_PREFIX.test(tag)) return tag;
  }
  return 'dialog';
}

function fieldName(error: Element): string {
  const field = error.closest('mat-form-field, .form-group, label') ?? error.parentElement;
  const input = field?.querySelector('[formcontrolname], [name]');
  return scrub(input?.getAttribute('formcontrolname') || input?.getAttribute('name') || hostOf(error) || 'field', 60);
}

/** Runs `work`, swallowing whatever it throws: usage tracking must never break the page it watches. */
function quietly<A extends unknown[]>(work: (...args: A) => void): (...args: A) => void {
  return (...args: A) => {
    try {
      work(...args);
    } catch {
      // ignored on purpose
    }
  };
}

/** A random id, also on older web views without crypto.randomUUID. */
function newId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    // fall through
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

/** Waits for `promise`, but no longer than `ms`; then `fallback`. Never rejects. */
function within<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise<T>((resolve) => {
    const timer = setTimeout(() => resolve(fallback), ms);
    promise.then((value) => { clearTimeout(timer); resolve(value); }, () => { clearTimeout(timer); resolve(fallback); });
  });
}

function readSession(now: number): string {
  try {
    const saved = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null') as { id: string; last: number } | null;
    const id = saved && typeof saved.id === 'string' && now - saved.last < SESSION_IDLE_MS ? saved.id : newId();
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, last: now }));
    return id;
  } catch {
    return newId();
  }
}

function readUnsent(): string[] {
  try {
    const value = JSON.parse(localStorage.getItem(UNSENT_KEY) || '[]') as unknown;
    return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

/** Keeps the newest batches that fit the limits; older ones are dropped. */
function writeUnsent(bodies: string[]): void {
  try {
    const kept: string[] = [];
    let size = 0;
    for (const body of [...bodies].reverse()) {
      // Measured as stored: each batch is a JSON string inside the stored array.
      const stored = JSON.stringify(body).length + 1;
      if (kept.length >= MAX_UNSENT || size + stored > MAX_UNSENT_CHARS) break;
      kept.unshift(body);
      size += stored;
    }
    if (kept.length) localStorage.setItem(UNSENT_KEY, JSON.stringify(kept));
    else localStorage.removeItem(UNSENT_KEY);
  } catch {
    // Storage can be full or blocked; the batch is then lost, which is acceptable for usage data.
  }
}

export function createUsageTracker(options: UsageTrackerOptions): UsageTracker {
  const now = options.now ?? (() => Date.now());
  const route = (): string => {
    try {
      return options.route ? options.route() : routePattern(location.pathname);
    } catch {
      return '/';
    }
  };
  const zones = options.textZones ?? DEFAULT_TEXT_ZONES;

  let queue: UsageEvent[] = [];
  const apiTotals = new Map<string, { n: number; ms: number; slow: number; fail: number }>();
  let lastSent = 0;
  let running = false;
  let flushing = false;

  let pass: string | null = null;
  let passExp = 0;
  let passDisabled = false;
  let passAskedAt = -Infinity;
  let passRequest: Promise<string | null> | null = null;
  /** Bumped when the person or practice changes, so a pass asked for before then is not kept. */
  let passGeneration = 0;

  const clock = new ActiveClock();
  const rage = new RageClicks();
  let view = { route: '', at: 0, segments: 0 };
  let seenInView = new Set<string>();
  let errorsInView = 0;

  const pendingClicks: { event: UsageEvent; changed: boolean }[] = [];
  const dialogs = new Map<Element, { name: string; at: number; route: string; done: boolean }>();
  /** Watches for dialogs and validation messages being added. */
  let observer: MutationObserver | null = null;
  /** Watches every change on screen, only while a click is waiting to be told apart from a dead one. */
  let clickObserver: MutationObserver | null = null;

  const push = (event: UsageEvent) => {
    queue.push(event);
    if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
    if (queue.length >= BATCH_SIZE || now() - lastSent >= BATCH_EVERY_MS) void flush();
  };

  const closeView = (continuing: boolean) => {
    if (!view.route) return;
    const ms = clock.take(now());
    // A later part of a page with no time on it says nothing new.
    if (view.segments && !ms) return;
    queue.push({ t: 'view', at: view.at, r: view.route, ms, ...(view.segments ? { c: 1 } : {}) });
    view.segments = continuing ? view.segments + 1 : 0;
  };

  async function currentPass(): Promise<string | null> {
    if (passDisabled) return null;
    if (pass && passExp - now() > PASS_MARGIN_MS) return pass;
    if (now() - passAskedAt < PASS_RETRY_MS && !pass) return null;
    passRequest ??= (async () => {
      passAskedAt = now();
      const generation = passGeneration;
      try {
        const fresh = await within<string | null | undefined>(Promise.resolve().then(() => options.getPass()), WAIT_MS, undefined);
        if (generation !== passGeneration) return null;
        // No answer in time: tried again later, as after a failure.
        if (fresh === undefined) return pass && passExp > now() ? pass : null;
        if (!fresh) {
          passDisabled = true;
          pass = null;
          return null;
        }
        pass = fresh;
        passExp = passExpiry(fresh);
        return fresh;
      } catch {
        return pass && passExp > now() ? pass : null;
      } finally {
        if (generation === passGeneration) passRequest = null;
      }
    })();
    return passRequest;
  }

  const takeBatch = (): UsageEvent[] => {
    const at = now();
    for (const [key, total] of apiTotals) queue.push({ t: 'api', at, r: '', el: key, ...total });
    apiTotals.clear();
    const events = queue;
    queue = [];
    lastSent = at;
    return events;
  };

  const envelope = (passValue: string, events: UsageEvent[]) => JSON.stringify({
    pass: passValue, app: options.appVersion, vp: viewportBucket(typeof window !== 'undefined' ? window.innerWidth : 0), s: readSession(now()), events,
  });

  /** A kept batch made before any pass was held takes the current one. */
  const withPass = (body: string, passValue: string): string => {
    try {
      const parsed = JSON.parse(body) as { pass?: string };
      return parsed.pass ? body : JSON.stringify({ ...parsed, pass: passValue });
    } catch {
      return body;
    }
  };

  /** One batch to REMOAT, opaque (no-cors), given up on after a while. True when sent. */
  const post = async (body: string): Promise<boolean> => {
    try {
      const abort = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = setTimeout(() => abort?.abort(), WAIT_MS);
      try {
        const sent = fetch(options.endpoint, {
          method: 'POST', mode: 'no-cors', keepalive: body.length < 60_000, headers: { 'Content-Type': 'text/plain' }, body,
          ...(abort ? { signal: abort.signal } : {}),
        });
        return await within(sent.then(() => true, () => false), WAIT_MS + 1000, false);
      } finally {
        clearTimeout(timer);
      }
    } catch {
      return false;
    }
  };

  async function flush(): Promise<void> {
    if (flushing) return;
    flushing = true;
    try {
      if (!queue.length && !apiTotals.size) return;
      const events = takeBatch();
      const passValue = await currentPass();
      if (!passValue) return;
      const bodies = [...readUnsent().map((body) => withPass(body, passValue)), envelope(passValue, events)];
      const failed: string[] = [];
      for (const body of bodies) if (!(await post(body))) failed.push(body);
      writeUnsent(failed);
    } catch {
      // ignored on purpose: usage data is never worth a failure on the page
    } finally {
      flushing = false;
    }
  }

  const beacon = (body: string): boolean => {
    try {
      return typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function'
        && navigator.sendBeacon(options.endpoint, new Blob([body], { type: 'text/plain' }));
    } catch {
      return false;
    }
  };

  /** The tab is going away: send by beacon with the pass already held, or keep the batch for next time. */
  const flushByBeacon = quietly(() => {
    closeView(true);
    clock.pause(now());
    if (!queue.length && !apiTotals.size) return;
    const events = takeBatch();
    if (passDisabled) return;
    const held = pass && passExp > now() ? pass : null;
    if (!held) {
      // No pass yet: keep the batch without one; the next send adds the pass.
      writeUnsent([...readUnsent(), envelope('', events)]);
      return;
    }
    const bodies = [...readUnsent().map((body) => withPass(body, held)), envelope(held, events)];
    writeUnsent(bodies.filter((body) => !beacon(body)));
  });

  const onActivity = quietly(() => clock.activity(now()));

  const watchClicks = () => {
    if (clickObserver || typeof MutationObserver !== 'function') return;
    clickObserver = new MutationObserver(quietly(() => { for (const pending of pendingClicks) pending.changed = true; }));
    clickObserver.observe(document.body, { childList: true, subtree: true, attributes: true, characterData: true });
  };

  const onClick = quietly((event: MouseEvent) => {
    if (!(event.target instanceof Element)) return;
    clock.activity(now());
    const { name, interactive } = describeElement(event.target, zones);
    const record: UsageEvent = { t: 'click', at: now(), r: view.route || route(), el: name };
    if (!interactive) record['i'] = 0;
    if (rage.click(event.clientX, event.clientY, now())) record['rage'] = 1;
    for (const [dialog, open] of dialogs) {
      if (dialog.contains(event.target) && isConfirming(event.target.closest('button, [role=button], a'))) open.done = true;
    }
    const pending = { event: record, changed: false };
    pendingClicks.push(pending);
    watchClicks();
    setTimeout(quietly(() => {
      pendingClicks.splice(pendingClicks.indexOf(pending), 1);
      if (!pendingClicks.length) {
        clickObserver?.disconnect();
        clickObserver = null;
      }
      if (!pending.changed) record['dead'] = 1;
      push(record);
    }), DEAD_AFTER_MS);
  });

  const onMutations = quietly((records: MutationRecord[]) => {
    for (const record of records) {
      record.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        const found = node.matches('[role=dialog]') ? [node] : Array.from(node.querySelectorAll('[role=dialog]'));
        for (const dialog of found) dialogs.set(dialog, { name: dialogName(dialog), at: now(), route: view.route || route(), done: false });
        const errors = node.matches('mat-error') ? [node] : Array.from(node.querySelectorAll('mat-error'));
        for (const error of errors) {
          const field = fieldName(error);
          if (seenInView.has(`invalid:${field}`)) continue;
          seenInView.add(`invalid:${field}`);
          push({ t: 'invalid', at: now(), r: view.route || route(), el: field });
        }
      });
    }
    for (const [dialog, open] of dialogs) {
      if (dialog.isConnected) continue;
      dialogs.delete(dialog);
      push({ t: 'dialog', at: open.at, r: open.route, el: open.name, ms: now() - open.at, done: open.done ? 1 : 0 });
    }
  });

  const onVisibility = quietly(() => {
    if (document.visibilityState === 'hidden') flushByBeacon();
    else clock.activity(now());
  });
  const onPageHide = quietly(() => flushByBeacon());
  const onError = quietly((event: ErrorEvent) => tracker.error(event.error ?? event.message));
  const onRejection = quietly((event: PromiseRejectionEvent) => tracker.error(event.reason));

  const tracker: UsageTracker = {
    start: quietly(() => {
      if (running) return;
      running = true;
      lastSent = now();
      view = { route: route(), at: now(), segments: 0 };
      clock.activity(now());
      document.addEventListener('click', onClick, true);
      for (const type of ['keydown', 'scroll', 'pointerdown', 'touchstart']) document.addEventListener(type, onActivity, { capture: true, passive: true });
      document.addEventListener('visibilitychange', onVisibility);
      window.addEventListener('pagehide', onPageHide);
      window.addEventListener('error', onError);
      window.addEventListener('unhandledrejection', onRejection);
      if (typeof MutationObserver === 'function') {
        observer = new MutationObserver(onMutations);
        observer.observe(document.body, { childList: true, subtree: true });
      }
      void currentPass().catch(() => null);
    }),

    stop: quietly(() => {
      if (!running) return;
      running = false;
      document.removeEventListener('click', onClick, true);
      for (const type of ['keydown', 'scroll', 'pointerdown', 'touchstart']) document.removeEventListener(type, onActivity, { capture: true });
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
      observer?.disconnect();
      observer = null;
      clickObserver?.disconnect();
      clickObserver = null;
    }),

    navigated: quietly(() => {
      const next = route();
      if (next === view.route) return;
      closeView(false);
      view = { route: next, at: now(), segments: 0 };
      seenInView = new Set();
      errorsInView = 0;
      clock.activity(now());
    }),

    flow: quietly((name: string, stage: FlowStage) => {
      push({ t: 'flow', at: now(), r: view.route || route(), el: scrub(String(name), 60), st: stage });
    }),

    action: quietly((name: string) => {
      push({ t: 'click', at: now(), r: view.route || route(), el: scrub(String(name), 80) });
    }),

    api: quietly((method: string, url: string, ms: number, status: number) => {
      let path = String(url);
      try {
        path = new URL(path, 'http://x').pathname;
      } catch {
        // keep the url as given
      }
      const key = `${String(method).toUpperCase()} ${routePattern(path)}`.slice(0, 120);
      const total = apiTotals.get(key) ?? { n: 0, ms: 0, slow: 0, fail: 0 };
      total.n += 1;
      total.ms += Math.round(Number(ms) || 0);
      if (ms >= SLOW_MS) total.slow += 1;
      if (status === 0 || status >= 500) total.fail += 1;
      apiTotals.set(key, total);
    }),

    error: quietly((error: unknown) => {
      if (errorsInView >= MAX_ERRORS_PER_PAGE) return;
      const name = error instanceof Error ? error.name : 'Error';
      const message = error instanceof Error ? error.message : String(error ?? '');
      const label = scrub(`${name}: ${message}`, 160);
      if (seenInView.has(`error:${label}`)) return;
      seenInView.add(`error:${label}`);
      errorsInView += 1;
      push({ t: 'error', at: now(), r: view.route || route(), el: label });
    }),

    passChanged: quietly(() => {
      // Batches kept without a pass belong to whoever was signed in before; they are not sent under the new pass.
      writeUnsent(readUnsent().filter((body) => !body.includes('"pass":""')));
      pass = null;
      passExp = 0;
      passDisabled = false;
      passAskedAt = -Infinity;
      passGeneration += 1;
      passRequest = null;
      // Fetched now, so a tab closed straight after still has a pass to send with.
      if (running) void currentPass().catch(() => null);
    }),

    flush: () => flush().catch(() => undefined),
  };
  return tracker;
}
